import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Fab,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  SettingsOutlined,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Web as WebIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  Launch as LaunchIcon,
  FormatColorText as FormatColorTextIcon,
  Palette as PaletteIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/useAuth'

// Types
interface LandingPageLink {
  Id: number
  Title: string
  Description: string | null
  Route: string
  RequiredGroup: string
  Visibility: string
  DisplayOrder: number
  Enabled: boolean
  Icon: string | null
  IsSystemLink: boolean
  KeyFeatures: string | null  // JSON array of features
  ShowKeyFeatures: boolean    // Show/hide key features section
  // Text formatting fields
  TitleColor: string | null
  TitleFontFamily: string | null
  TitleFontSize: string | null
  TitleFontWeight: string | null
  TitleFontStyle: string | null
  TitleTextDecoration: string | null
  DescriptionColor: string | null
  DescriptionFontFamily: string | null
  DescriptionFontSize: string | null
  DescriptionFontWeight: string | null
  DescriptionFontStyle: string | null
  DescriptionTextDecoration: string | null
  CreatedBy: string | null
  CreatedDate: string | null
  ModifiedBy: string | null
  ModifiedDate: string | null
  Deleted: number | null
}

interface LandingPageLinkForm {
  Title: string
  Description: string
  Route: string
  RequiredGroup: string
  Visibility: string
  DisplayOrder: number
  Enabled: boolean
  Icon: string
  IsSystemLink: boolean
  KeyFeatures: string        // JSON string of features
  ShowKeyFeatures: boolean   // Show/hide key features section
  // Text formatting fields
  TitleColor: string
  TitleFontFamily: string
  TitleFontSize: string
  TitleFontWeight: string
  TitleFontStyle: string
  TitleTextDecoration: string
  DescriptionColor: string
  DescriptionFontFamily: string
  DescriptionFontSize: string
  DescriptionFontWeight: string
  DescriptionFontStyle: string
  DescriptionTextDecoration: string
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Enhanced icon mapping with professional/Office icons
const iconMap: Record<string, React.ReactElement> = {
  // Basic icons
  LinkIcon: <LinkIcon />,
  TrendingUpIcon: <TrendingUpIcon />,
  AnalyticsIcon: <AnalyticsIcon />,
  SecurityIcon: <SecurityIcon />,
  DashboardIcon: <DashboardIcon />,
  AssessmentIcon: <AssessmentIcon />,
  
  // Business & Office icons
  BusinessIcon: <BusinessIcon />,
  WorkIcon: <WorkIcon />,
  DescriptionIcon: <DescriptionIcon />,
  TableChartIcon: <TableChartIcon />,
  BarChartIcon: <BarChartIcon />,
  EmailIcon: <EmailIcon />,
  CalendarIcon: <CalendarIcon />,
  
  // Technology icons
  StorageIcon: <StorageIcon />,
  CloudIcon: <CloudIcon />,
  ComputerIcon: <ComputerIcon />,
  PhoneIcon: <PhoneIcon />,
  WebIcon: <WebIcon />,
  LanguageIcon: <LanguageIcon />,
  PublicIcon: <PublicIcon />,
  LaunchIcon: <LaunchIcon />
}

// Icon options with names and visual icons
const iconOptions = [
  { value: 'LinkIcon', name: 'Link', icon: <LinkIcon /> },
  { value: 'TrendingUpIcon', name: 'Trending Up', icon: <TrendingUpIcon /> },
  { value: 'AnalyticsIcon', name: 'Analytics', icon: <AnalyticsIcon /> },
  { value: 'SecurityIcon', name: 'Security', icon: <SecurityIcon /> },
  { value: 'DashboardIcon', name: 'Dashboard', icon: <DashboardIcon /> },
  { value: 'AssessmentIcon', name: 'Assessment', icon: <AssessmentIcon /> },
  { value: 'BusinessIcon', name: 'Business', icon: <BusinessIcon /> },
  { value: 'WorkIcon', name: 'Work/Office', icon: <WorkIcon /> },
  { value: 'DescriptionIcon', name: 'Document', icon: <DescriptionIcon /> },
  { value: 'TableChartIcon', name: 'Spreadsheet', icon: <TableChartIcon /> },
  { value: 'BarChartIcon', name: 'Presentation', icon: <BarChartIcon /> },
  { value: 'EmailIcon', name: 'Email/Outlook', icon: <EmailIcon /> },
  { value: 'CalendarIcon', name: 'Calendar', icon: <CalendarIcon /> },
  { value: 'StorageIcon', name: 'Database', icon: <StorageIcon /> },
  { value: 'CloudIcon', name: 'Cloud/OneDrive', icon: <CloudIcon /> },
  { value: 'ComputerIcon', name: 'Computer', icon: <ComputerIcon /> },
  { value: 'PhoneIcon', name: 'Mobile/Teams', icon: <PhoneIcon /> },
  { value: 'WebIcon', name: 'Web App', icon: <WebIcon /> },
  { value: 'LanguageIcon', name: 'Website', icon: <LanguageIcon /> },
  { value: 'PublicIcon', name: 'Public/SharePoint', icon: <PublicIcon /> },
  { value: 'LaunchIcon', name: 'External Link', icon: <LaunchIcon /> }
]

// Font options for text formatting
const fontFamilyOptions = [
  'Arial, sans-serif',
  'Helvetica, sans-serif',
  'Georgia, serif',
  'Times New Roman, serif',
  'Verdana, sans-serif',
  'Tahoma, sans-serif',
  'Trebuchet MS, sans-serif',
  'Impact, sans-serif',
  'Courier New, monospace'
]

const fontSizeOptions = [
  '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'
]

const fontWeightOptions = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: '100', label: 'Thin' },
  { value: '300', label: 'Light' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi Bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
  { value: '900', label: 'Black' }
]

const fontStyleOptions = [
  { value: 'normal', label: 'Normal' },
  { value: 'italic', label: 'Italic' },
  { value: 'oblique', label: 'Oblique' }
]

const textDecorationOptions = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Underline' },
  { value: 'overline', label: 'Overline' },
  { value: 'line-through', label: 'Line Through' }
]

// Sortable Row Component
interface SortableRowProps {
  link: LandingPageLink
  onEdit: (link: LandingPageLink) => void
  onDelete: (linkId: number, linkTitle: string) => void
  getPermissionColor: (group: string) => 'error' | 'warning' | 'info' | 'success' | 'default'
  getVisibilityIcon: (visibility: string) => React.ReactElement
  iconMap: Record<string, React.ReactElement>
}

const SortableRow: React.FC<SortableRowProps> = ({ 
  link, 
  onEdit, 
  onDelete, 
  getPermissionColor, 
  getVisibilityIcon, 
  iconMap 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.Id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ 
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <DragIcon sx={{ color: 'action.active' }} />
          </Box>
          {link.DisplayOrder}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {iconMap[link.Icon || 'LinkIcon'] || <LinkIcon />}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {link.Title}
            </Typography>
            {link.Description && (
              <Typography variant="caption" color="text.secondary">
                {link.Description}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {link.Route}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={link.RequiredGroup}
          color={getPermissionColor(link.RequiredGroup) as any}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getVisibilityIcon(link.Visibility)}
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {link.Visibility}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={link.Enabled ? 'Enabled' : 'Disabled'}
          color={link.Enabled ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Chip
          label={link.IsSystemLink ? 'System' : 'Custom'}
          color={link.IsSystemLink ? 'warning' : 'info'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(link)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={link.IsSystemLink ? 'Cannot delete system links' : 'Delete'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={link.IsSystemLink}
                onClick={() => !link.IsSystemLink && onDelete(link.Id, link.Title)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export const LandingConfigPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, userPermissions, logout, getAccessToken } = useAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<LandingPageLink | null>(null)
  const [formData, setFormData] = useState<LandingPageLinkForm>({
    Title: '',
    Description: '',
    Route: '',
    RequiredGroup: 'sec-fabric-read',
    Visibility: 'visible',
    DisplayOrder: 0,
    Enabled: true,
    Icon: 'LinkIcon',
    IsSystemLink: false,
    KeyFeatures: '',
    ShowKeyFeatures: true,
    // Text formatting fields
    TitleColor: '',
    TitleFontFamily: '',
    TitleFontSize: '',
    TitleFontWeight: '',
    TitleFontStyle: '',
    TitleTextDecoration: '',
    DescriptionColor: '',
    DescriptionFontFamily: '',
    DescriptionFontSize: '',
    DescriptionFontWeight: '',
    DescriptionFontStyle: '',
    DescriptionTextDecoration: ''
  })
  const [saving, setSaving] = useState(false)
  const [links, setLinks] = useState<LandingPageLink[]>([])
  const [loading, setLoading] = useState(true)

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      handleClose()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      Title: '',
      Description: '',
      Route: '',
      RequiredGroup: 'sec-fabric-read',
      Visibility: 'visible',
      DisplayOrder: 0,
      Enabled: true,
      Icon: 'LinkIcon',
      IsSystemLink: false,
      KeyFeatures: '',
      ShowKeyFeatures: true,
      // Text formatting fields
      TitleColor: '',
      TitleFontFamily: '',
      TitleFontSize: '',
      TitleFontWeight: '',
      TitleFontStyle: '',
      TitleTextDecoration: '',
      DescriptionColor: '',
      DescriptionFontFamily: '',
      DescriptionFontSize: '',
      DescriptionFontWeight: '',
      DescriptionFontStyle: '',
      DescriptionTextDecoration: ''
    })
  }

  const handleOpenDialog = (link?: LandingPageLink) => {
    if (link) {
      setEditingLink(link)
      setFormData({
        Title: link.Title,
        Description: link.Description || '',
        Route: link.Route,
        RequiredGroup: link.RequiredGroup,
        Visibility: link.Visibility,
        DisplayOrder: link.DisplayOrder,
        Enabled: link.Enabled,
        Icon: link.Icon || 'LinkIcon',
        IsSystemLink: link.IsSystemLink,
        KeyFeatures: link.KeyFeatures || '',
        ShowKeyFeatures: link.ShowKeyFeatures !== false,
        // Text formatting fields
        TitleColor: link.TitleColor || '',
        TitleFontFamily: link.TitleFontFamily || '',
        TitleFontSize: link.TitleFontSize || '',
        TitleFontWeight: link.TitleFontWeight || '',
        TitleFontStyle: link.TitleFontStyle || '',
        TitleTextDecoration: link.TitleTextDecoration || '',
        DescriptionColor: link.DescriptionColor || '',
        DescriptionFontFamily: link.DescriptionFontFamily || '',
        DescriptionFontSize: link.DescriptionFontSize || '',
        DescriptionFontWeight: link.DescriptionFontWeight || '',
        DescriptionFontStyle: link.DescriptionFontStyle || '',
        DescriptionTextDecoration: link.DescriptionTextDecoration || ''
      })
    } else {
      setEditingLink(null)
      resetForm()
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingLink(null)
    resetForm()
  }

  const getPermissionColor = (group: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    switch (group) {
      case 'sec-fabric-admin':
      case 'SQL_Admin': 
        return 'error'
      case 'sec-fabric-read-write':
      case 'SQL_Writers': 
        return 'warning'
      case 'sec-fabric-read':
      case 'SQL_Readers': 
        return 'info'
      case 'All': 
        return 'success'
      default: 
        return 'default'
    }
  }

  const getVisibilityIcon = (visibility: string): React.ReactElement => {
    switch (visibility) {
      case 'visible': return <VisibilityIcon color="success" />
      case 'greyed': return <VisibilityIcon color="warning" />
      case 'hidden': return <VisibilityOffIcon color="error" />
      default: return <VisibilityIcon />
    }
  }

  // Load links from API
  const loadLinks = async () => {
    try {
      setLoading(true)
      const token = await getAccessToken()
      
      if (!token) {
        console.error('No access token available')
        setLinks([])
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/landing-links`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setLinks(data.items || [])
      } else {
        console.error('Failed to load links:', response.statusText)
        // Use empty array if API fails
        setLinks([])
      }
    } catch (error) {
      console.error('Error loading links:', error)
      // Use empty array if API fails
      setLinks([])
    } finally {
      setLoading(false)
    }
  }

  // Save link to API
  const saveLink = async () => {
    if (!formData.Title.trim() || !formData.Route.trim()) {
      alert('Title and Route are required')
      return
    }

    try {
      setSaving(true)
      
      const token = await getAccessToken()
      
      if (!token) {
        console.error('No access token available')
        alert('Authentication error. Please try logging in again.')
        return
      }
      
      // Determine if we're creating or updating
      const isUpdate = editingLink !== null
      const url = isUpdate 
        ? `${API_BASE_URL}/api/v1/admin/landing-links/${editingLink.Id}`
        : `${API_BASE_URL}/api/v1/admin/landing-links`
      const method = isUpdate ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log(`Link ${isUpdate ? 'updated' : 'created'} successfully:`, result)
        
        // Reload links to show the changes
        await loadLinks()
        
        // Close dialog
        handleCloseDialog()
      } else {
        const errorData = await response.json()
        console.error(`Failed to ${isUpdate ? 'update' : 'create'} link:`, errorData)
        alert(`Failed to ${isUpdate ? 'update' : 'create'} link: ${errorData.detail || response.statusText}`)
      }
    } catch (error) {
      console.error(`Error ${editingLink ? 'updating' : 'creating'} link:`, error)
      alert(`Error ${editingLink ? 'updating' : 'creating'} link. Please try again.`)
    } finally {
      setSaving(false)
    }
  }

  // Delete link from API
  const deleteLink = async (linkId: number, linkTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${linkTitle}"? This action cannot be undone.`)) {
      return
    }

    try {
      const token = await getAccessToken()
      
      if (!token) {
        console.error('No access token available')
        alert('Authentication error. Please try logging in again.')
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/landing-links/${linkId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        console.log('Link deleted successfully')
        
        // Reload links to reflect the deletion
        await loadLinks()
      } else {
        const errorData = await response.json()
        console.error('Failed to delete link:', errorData)
        alert(`Failed to delete link: ${errorData.detail || response.statusText}`)
      }
    } catch (error) {
      console.error('Error deleting link:', error)
      alert('Error deleting link. Please try again.')
    }
  }

  // Reorder links via API
  const reorderLinks = async (linkOrders: { id: number; displayOrder: number }[]) => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        console.error('No access token available')
        alert('Authentication error. Please try logging in again.')
        // Reload links to revert changes
        await loadLinks()
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/landing-links/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ link_orders: linkOrders })
      })
      
      if (response.ok) {
        console.log('Links reordered successfully')
      } else {
        const errorData = await response.json()
        console.error('Failed to reorder links:', errorData)
        alert(`Failed to reorder links: ${errorData.detail || response.statusText}`)
        // Reload links to revert changes
        await loadLinks()
      }
    } catch (error) {
      console.error('Error reordering links:', error)
      alert('Error reordering links. Please try again.')
      // Reload links to revert changes
      await loadLinks()
    }
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const sortedLinks = [...links].sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      const oldIndex = sortedLinks.findIndex((link) => link.Id === active.id)
      const newIndex = sortedLinks.findIndex((link) => link.Id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        // Reorder the array
        const newLinks = arrayMove(sortedLinks, oldIndex, newIndex)
        
        // Update display orders
        const linkOrders = newLinks.map((link, index) => ({
          id: link.Id,
          displayOrder: index + 1
        }))

        // Update local state immediately for better UX
        const updatedLinks = newLinks.map((link, index) => ({
          ...link,
          DisplayOrder: index + 1
        }))
        setLinks(updatedLinks)

        // Send to API
        await reorderLinks(linkOrders)
      }
    }
  }

  // Load links on component mount
  useEffect(() => {
    loadLinks()
  }, [])

  if (!userPermissions?.isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" textAlign="center">
          Access Denied
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mt: 2 }}>
          You do not have administrator privileges to access this page.
        </Typography>
      </Container>
    )
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Landing Page Configuration
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin')}
              sx={{ textTransform: 'none' }}
            >
              Back to Admin
            </Button>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.username || 'Admin'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {(user?.name || user?.username || 'A').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsOutlined color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 300 }}>
              Landing Page Configuration
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ textTransform: 'none' }}
            >
              Add New Link
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LinkIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {links.length}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Links
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VisibilityIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {links.filter((link: LandingPageLink) => link.Enabled && link.Visibility === 'visible').length}
                    </Typography>
                    <Typography color="text.secondary">
                      Active Links
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SecurityIcon color="warning" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {links.filter((link: LandingPageLink) => link.IsSystemLink).length}
                    </Typography>
                    <Typography color="text.secondary">
                      System Links
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssessmentIcon color="info" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {links.filter((link: LandingPageLink) => 
                        link.RequiredGroup === 'sec-fabric-admin' || link.RequiredGroup === 'SQL_Admin'
                      ).length}
                    </Typography>
                    <Typography color="text.secondary">
                      Admin Only
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Links Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No landing page links configured. Add some links to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={links.map(link => link.Id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {links
                        .sort((a: LandingPageLink, b: LandingPageLink) => a.DisplayOrder - b.DisplayOrder)
                        .map((link: LandingPageLink) => (
                          <SortableRow
                            key={link.Id}
                            link={link}
                            onEdit={handleOpenDialog}
                            onDelete={deleteLink}
                            getPermissionColor={getPermissionColor}
                            getVisibilityIcon={getVisibilityIcon}
                            iconMap={iconMap}
                          />
                        ))
                      }
                    </SortableContext>
                  </DndContext>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingLink ? 'Edit Landing Page Link' : 'Add New Landing Page Link'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Route"
                    value={formData.Route}
                    onChange={(e) => setFormData({ ...formData, Route: e.target.value })}
                    placeholder="/example-route or https://external-site.com"
                    required
                    helperText="Use /path for internal routes or https://... for external URLs"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.Description}
                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Required Permission</InputLabel>
                    <Select
                      value={formData.RequiredGroup}
                      onChange={(e) => setFormData({ ...formData, RequiredGroup: e.target.value })}
                      label="Required Permission"
                    >
                      <MenuItem value="All">All Users</MenuItem>
                      <MenuItem value="sec-fabric-read">Fabric Readers</MenuItem>
                      <MenuItem value="sec-fabric-read-write">Fabric Writers</MenuItem>
                      <MenuItem value="sec-fabric-admin">Fabric Admins</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Icon</InputLabel>
                    <Select
                      value={formData.Icon}
                      onChange={(e) => setFormData({ ...formData, Icon: e.target.value })}
                      label="Icon"
                      renderValue={(selected) => {
                        const option = iconOptions.find(opt => opt.value === selected)
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option?.icon}
                            {option?.name}
                          </Box>
                        )
                      }}
                    >
                      {iconOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ListItemIcon>
                            {option.icon}
                          </ListItemIcon>
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.Enabled}
                        onChange={(e) => setFormData({ ...formData, Enabled: e.target.checked })}
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.IsSystemLink}
                        onChange={(e) => setFormData({ ...formData, IsSystemLink: e.target.checked })}
                      />
                    }
                    label="System Link (Protected from deletion)"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveLink}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default LandingConfigPage
